
import React, { useState, useRef } from 'react';
import { Check, Maximize } from 'lucide-react';
import { Button } from './Button';
import ReactCrop, { type Crop, centerCrop, convertToPixelCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { zielMasse } from './imageCropperMasse';

interface Props {
  image: string;
  onSave: (croppedImage: string) => void;
  onCancel: () => void;
  /**
   * Festes Seitenverhältnis (Breite/Höhe) oder `null` = frei. Vorgabe 3:4 für
   * Fotos (passt zum Hochformat-Layout im PDF). Pläne übergeben `null`: ein
   * Querformat-Plan ließ sich sonst nur als fast quadratischer Ausschnitt
   * übernehmen (Betreiber 27.09.2026).
   */
  aspect?: number | null;
  titel?: string;
}

/** Fotos: 3:4 (Hochformat im PDF). */
const FOTO_SEITENVERHAELTNIS = 3 / 4;
function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  )
}

/** Ganzes Bild als Ausschnitt (freies Format). */
const GANZES_BILD: Crop = { unit: '%', x: 0, y: 0, width: 100, height: 100 };

export const ImageCropper: React.FC<Props> = ({ image, onSave, onCancel, aspect = FOTO_SEITENVERHAELTNIS, titel = 'Bild zuschneiden' }) => {
  const [crop, setCrop] = useState<Crop>();
  const imgRef = useRef<HTMLImageElement>(null);
  const frei = aspect === null;

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { width, height } = e.currentTarget;
    // Frei: zunächst das ganze Bild (meist will man den Plan komplett); fest: 90 % mittig.
    setCrop(frei ? GANZES_BILD : centerAspectCrop(width, height, aspect));
  }

  const handleSave = () => {
    const image = imgRef.current;
    // Der aktuelle Rahmen gilt — auch ohne Ziehen (vorher: ohne Ziehen passierte nichts).
    if (!crop || !image || !image.width || !image.height) return;
    const px = convertToPixelCrop(crop, image.width, image.height);
    if (px.width <= 0 || px.height <= 0) return;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const cropX = px.x * scaleX;
    const cropY = px.y * scaleY;
    const cropWidth = px.width * scaleX;
    const cropHeight = px.height * scaleY;
    const ziel = zielMasse(cropWidth, cropHeight);

    const canvas = document.createElement('canvas');
    canvas.width = ziel.breite;
    canvas.height = ziel.hoehe;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, cropX, cropY, cropWidth, cropHeight, 0, 0, ziel.breite, ziel.hoehe);

    onSave(canvas.toDataURL('image/jpeg', 0.9));
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900 flex flex-col animate-fade-in">
        <div className="bg-slate-800 p-4 flex justify-between items-center gap-3 text-white shrink-0 shadow-md z-10">
            <div className="min-w-0">
                <h3 className="font-bold text-lg">{titel}</h3>
                <p className="text-xs text-slate-400">
                    {frei
                        ? 'Rahmen an den Ecken ziehen — Hoch- oder Querformat, wie der Plan es braucht.'
                        : 'Ziehen Sie den Rahmen, um den Ausschnitt zu wählen.'}
                </p>
            </div>
            {frei && (
                <button
                    type="button"
                    onClick={() => setCrop(GANZES_BILD)}
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-slate-500 px-3 py-2 text-xs font-semibold text-slate-100 hover:bg-slate-700"
                >
                    <Maximize className="w-4 h-4" /> Ganzes Bild
                </button>
            )}
        </div>

        <div className="flex-1 bg-black p-4 flex items-center justify-center overflow-auto touch-none">
            <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                aspect={frei ? undefined : aspect}
            >
                <img
                    ref={imgRef}
                    alt="Crop me"
                    src={image}
                    onLoad={onImageLoad}
                    style={{ maxHeight: '80vh', maxWidth: '100%', objectFit: 'contain' }}
                    crossOrigin="anonymous"
                />
            </ReactCrop>
        </div>
        {/* Fussleiste wie im ImageEditor: Aktionen immer sichtbar, unten. */}
        <div className="bg-slate-800 border-t border-slate-700 p-3 md:p-4 shrink-0 grid grid-cols-2 gap-3 sm:flex sm:justify-end pb-safe">
            <Button variant="ghost" onClick={onCancel} className="text-white hover:bg-slate-700 w-full sm:w-auto justify-center">Abbrechen</Button>
            <Button variant="primary" onClick={handleSave} icon={<Check className="w-4 h-4"/>} className="w-full sm:w-auto justify-center">Übernehmen</Button>
        </div>
    </div>
  );
};
